import { useTranslation } from "react-i18next"

import type { SkillPackageInstallPreview } from "@/api/admin/skill-packages"

import { AlertTriangle } from "@/components/icons"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"

import { skillInstallPreviewWarnings } from "../utils/skill-packages"

/**
 * What installing a skill package would change (the dry-run preview), with an
 * explicit confirm. Any warning the backend attaches to the preview — e.g. the
 * agent has no local tools, so the package's scripts will not run — is shown
 * above the diff, before the admin commits.
 */
export function HostedAgentSkillInstallDiffCard({
  preview,
  onConfirm,
  onCancel,
}: {
  preview: SkillPackageInstallPreview
  onConfirm: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const { diff, eval_cases: evalCases } = preview
  const actionChanges = Object.entries(diff.tool_actions_changes)
  const warnings = skillInstallPreviewWarnings(preview, (key, fallback) =>
    t(key, { defaultValue: fallback })
  )
  return (
    <div className="space-y-3 rounded-md border p-3">
      {warnings.length > 0 ? (
        <Alert
          className="border-foreground bg-warning text-warning-foreground [&>svg]:text-warning-foreground"
          data-testid="skill-install-preview-warning"
        >
          <AlertTriangle className="size-4" />
          <AlertTitle>
            {t("adminDashboard.hostedAgents.skillsTab.previewWarningTitle", {
              defaultValue: "Check before installing",
            })}
          </AlertTitle>
          <AlertDescription>
            {warnings.length === 1 ? (
              <p className="wrap-anywhere">{warnings[0]}</p>
            ) : (
              <ul className="list-disc space-y-1 pl-5">
                {warnings.map((warning) => (
                  <li key={warning} className="wrap-anywhere">
                    {warning}
                  </li>
                ))}
              </ul>
            )}
          </AlertDescription>
        </Alert>
      ) : null}
      <p className="text-sm font-medium">
        {t("adminDashboard.hostedAgents.skillsTab.diffTitle", {
          defaultValue: "Installing {{name}} v{{version}} will:",
          name: preview.package.name,
          version: preview.package.version,
        })}
      </p>
      <ul className="text-muted-foreground list-disc space-y-1 pl-5 text-sm">
        {diff.tools_to_add.length > 0 ? (
          <li>
            {t("adminDashboard.hostedAgents.skillsTab.diffToolsToAdd", {
              defaultValue: "Grant tools: {{tools}}",
              tools: diff.tools_to_add.join(", "),
            })}
          </li>
        ) : (
          <li>
            {t("adminDashboard.hostedAgents.skillsTab.diffNoNewTools", {
              defaultValue: "Grant no new tools",
            })}
          </li>
        )}
        {actionChanges.map(([tool, actions]) => (
          <li key={tool}>
            {t("adminDashboard.hostedAgents.skillsTab.diffActionChange", {
              defaultValue: "Set allowed actions on {{tool}}: {{actions}}",
              tool,
              actions: actions.join(", "),
            })}
          </li>
        ))}
        <li>
          {t("adminDashboard.hostedAgents.skillsTab.diffEvalSeeding", {
            defaultValue: "Seed {{count}} eval case(s)",
            count: evalCases.will_seed.length,
          })}
          {evalCases.will_skip_over_cap.length > 0
            ? " " +
              t("adminDashboard.hostedAgents.skillsTab.diffEvalSkipped", {
                defaultValue: "({{count}} skipped — the agent's eval suite is full)",
                count: evalCases.will_skip_over_cap.length,
              })
            : null}
        </li>
        {diff.requires_load_skill ? (
          <li className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1">
            {t("adminDashboard.hostedAgents.skillsTab.diffNeedsLoadSkill", {
              defaultValue:
                "The agent lacks the load_skill tool — grant it in the Tools tab or the package stays unreachable.",
            })}
          </li>
        ) : null}
        {diff.required_tools_missing.length > 0 ? (
          <li className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1">
            {t("adminDashboard.hostedAgents.skillsTab.diffRequiredMissing", {
              defaultValue: "Required tools still missing: {{tools}}",
              tools: diff.required_tools_missing.join(", "),
            })}
          </li>
        ) : null}
      </ul>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          {t("adminDashboard.hostedAgents.skillsTab.cancel", { defaultValue: "Cancel" })}
        </Button>
        <Button size="sm" onClick={onConfirm}>
          {t("adminDashboard.hostedAgents.skillsTab.confirmInstall", {
            defaultValue: "Install package",
          })}
        </Button>
      </div>
    </div>
  )
}
